<template lang="html">
  <section v-if="project && crafts && user" :style="themableStyle" class="theme-color">
    <div class="container">
      <section class="hero is-white is-small has-bg-img">
        <!-- Hero body -->
        <div class="hero-body">
          <div class="columns">
            <!-- Hero Left -->
            <div class="column is-1 is-paddingless pt-3">
              <router-link :to="{ path: `/users/${user.username}` }">
                <figure class="image avatar">
                  <img style="border-radius: 50%" :src="user.avatar.url" />
                </figure>
              </router-link>
            </div>

            <!-- Hero Center -->
            <div class="column is-paddingless pt-3 pl-3">
              <!-- Title  -->
              <p class="has-text-weight-semibold has-text-dark-primary is-size-4">
                {{ project.title }}
                <b-tooltip
                  v-if="project.hidden"
                  append-to-body
                  position="is-right"
                  label="Private"
                  type="is-warning"
                >
                  <b-icon icon="lock"></b-icon>
                </b-tooltip>
                <b-tooltip
                  v-if="!project.hidden"
                  append-to-body
                  position="is-right"
                  label="Public"
                  type="is-success"
                >
                  <b-icon icon="earth"></b-icon>
                </b-tooltip>
              </p>

              <!-- Subject -->
              <p class="is-size-5">
                <span v-if="project.character">{{ project.character }} </span>
                <span v-if="project.version">ver. {{ project.version }} </span>
                <span v-if="project.universe">from {{ project.universe }} </span>
              </p>

              <!-- Author -->
              <p class="is-size-5">
                by
                <router-link aria-current="page" :to="{ path: `/users/${user.username}` }"
                  ><span class="has-text-dark-primary has-text-weight-semibold">{{
                    user.cosplayer_name
                  }}</span></router-link
                >
              </p>

              <!-- Time -->
              <p class="is-size-7 is-italic is-bottom has-text-gray">
                Created {{ dateFromNow(project.created_at) }} - Last update
                {{ dateFromNow(project.updated_at) }}
              </p>
            </div>

            <!-- Hero Right -->
            <div v-if="isLoggedIn" class="column is-narrow">
              <!-- Edit button -->
              <span class="p-1" v-if="owner">
                <b-tooltip
                  label="Edit project"
                  type="is-warning is-inverted"
                  position="is-left"
                >
                  <b-button
                    class="is-warning is-inverted"
                    @click="isUpdateProjectModalActive = true"
                  >
                    <b-icon icon="pencil"></b-icon> </b-button
                ></b-tooltip>
              </span>

              <!-- Add teacher button -->
              <add-teacher-button
                v-if="!owner"
                :key="user.username"
                :teacher="user"
                :user="currentUser.attributes.username"
              />

              <!-- Manage images -->
              <image-management
                v-if="owner"
                :imageable-id="project.id"
                :imageable-type="'project'"
                @imagesUpdated="images = $event"
              />

              <!-- Change theme -->
              <theme-changer
                v-if="owner"
                :theme="project.theme"
                :themable-id="project.id"
                :themable-type="'project'"
                @themeUpdated="project.theme.name = $event"
              />

              <!-- Add to collection button -->
              <add-to-collection
                :collectableType="'project'"
                :collectableId="project.id"
              />

              <!-- Reporting -->
              <span v-if="!owner" class="p-1">
                <b-dropdown aria-role="list" position="is-bottom-left">
                  <b-button role="button" slot="trigger" class="is-danger is-inverted">
                    <b-icon size="is-small" icon="block-helper"></b-icon>
                  </b-button>

                  <b-dropdown-item aria-role="listitem"
                    ><b-icon
                      size="is-small"
                      type="is-danger"
                      icon="account-cancel"
                    ></b-icon>
                    Block user</b-dropdown-item
                  >
                  <b-dropdown-item aria-role="listitem"
                    ><b-icon
                      size="is-small"
                      type="is-danger"
                      icon="alert-octagon"
                    ></b-icon>
                    Report user</b-dropdown-item
                  >
                </b-dropdown>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <!-- Navigation -->
          <nav class="breadcrumb" aria-label="breadcrumbs">
            <ul>
              <li>
                <router-link aria-current="page" :to="{ path: `/users/${user.username}` }"
                  >{{ user.cosplayer_name }}
                </router-link>
              </li>
              <li class="is-active">
                <a href="#" aria-current="page">{{ project.title }}</a>
              </li>
            </ul>
          </nav>
          <!-- Overview  -->

          <div class="box">
            <!-- Overview header -->
            <div class="columns is-centered has-text-centered">
              <div v-if="project.character" class="column">
                <attribute-title :title="'Character'" />
                <p class="subtitle has-text-weight-semibold">{{ project.character }}</p>
              </div>
              <div v-if="project.version" class="column">
                <attribute-title :title="'Version'" />
                <p class="subtitle has-text-weight-semibold">{{ project.version }}</p>
              </div>
              <div v-if="project.universe" class="column">
                <attribute-title :title="'Universe'" />
                <p class="subtitle has-text-weight-semibold">{{ project.universe }}</p>
              </div>
            </div>
            <!-- Picture -->
            <carousel-horizontal :images="images" />

            <!-- Overview body -->
            <div class="columns is-multiline is-centered">
              <div v-if="project.description" class="column has-text-centered is-11">
                <attribute-title :title="'Description'" />
                <p class="content">{{ project.description }}</p>
              </div>
              <div class="column is-11">
                <div class="columns has-text-left is-centered">
                  <div
                    v-if="project.skills && project.skills.length"
                    class="column is-narrow"
                  >
                    <attribute-title
                      :title="'Skills'"
                      :tooltip="'Skills used in the Crafts of this Project'"
                    />
                    <tag-list :tags="project.skills" :slice-index="3" />
                  </div>

                  <div class="column">
                    <attribute-title
                      :title="'Progress'"
                      :tooltip="'How far the Project has progressed'"
                    />
                    <!-- TODO Change number 1 by project.progress -->
                    <b-progress
                      v-bind:type="1 === 2 ? 'is-success' : 'is-primary'"
                      :value="1 * 50"
                      show-value
                    >
                      <span v-if="1 == 0">Draft</span>
                      <span v-if="1 == 1">Work in progress</span>
                      <span v-if="1 == 2">Completed</span>
                    </b-progress>
                  </div>

                  <div
                    v-if="project.language_list && project.language_list.length"
                    class="column is-narrow"
                  >
                    <attribute-title
                      :title="'Language(s)'"
                      :tooltip="'Languages used in the Crafts of this Project'"
                    />

                    <tag-list :tags="project.language_list" :slice-index="3" />
                  </div>

                  <div
                    v-if="project.tag_list && project.tag_list.length"
                    class="column is-narrow"
                  >
                    <attribute-title
                      :title="'Tags'"
                      :tooltip="'Keywords to qualify the Project'"
                    />

                    <tag-list :tags="project.tag_list" :slice-index="3" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Overview footer -->
          </div>

          <b-tabs type="is-toggle" expanded>
            <b-tab-item v-if="crafts">
              <!-- Crafts -->
              <template slot="header">
                <b-icon icon="wrench"></b-icon>
                <span class="has-text-weight-semibold">
                  Crafts <b-tag rounded> {{ crafts.length }} </b-tag>
                </span>
              </template>
              <h1 class="title is-size-4">
                Crafts ({{ crafts.length }})
                <b-tooltip
                  v-if="owner"
                  position="is-right"
                  type="is-success"
                  label="Add new Craft"
                >
                  <a @click="isNewCraftModalActive = true">
                    <b-icon size="is-small" type="is-success" icon="plus-thick"></b-icon>
                  </a>
                </b-tooltip>
              </h1>
              <h2 class="subtitle is-italic is-size-6">
                Items crafted that are part of the Project
              </h2>

              <!-- Crafts List-->
              <draggable
                v-model="crafts"
                @change="craftDragged"
                tag="div"
                class="columns is-multiline"
                :disabled="!owner"
              >
                <div class="column is-one-fifth" v-for="craft in crafts" :key="craft.id">
                  <craft-card :craft="craft" :owner="owner" />
                </div>
              </draggable>
            </b-tab-item>

            <b-tab-item>
              <template slot="header">
                <b-icon icon="cart"></b-icon>
                <span class="has-text-weight-semibold">
                  Purchases <b-tag rounded> {{ purchases.length }} </b-tag>
                </span>
              </template>

              <!-- Purchases -->
              <h1 class="title is-size-4">
                Purchases ({{ purchases.length }})
                <b-tooltip
                  v-if="owner"
                  position="is-right"
                  type="is-success"
                  label="Add new Purchase"
                >
                  <a @click="isNewPurchaseModalActive = true">
                    <b-icon size="is-small" type="is-success" icon="plus-thick"></b-icon>
                  </a>
                </b-tooltip>
              </h1>
              <h2 class="subtitle is-italic is-size-6">
                Items purchased that are part of the Project
              </h2>

              <!-- Purchases List-->

              <draggable
                v-model="purchases"
                @change="purchaseDragged"
                tag="div"
                class="columns is-multiline"
                :disabled="!owner"
              >
                <div
                  class="column is-one-fifth"
                  v-for="purchase in purchases"
                  :key="purchase.id"
                >
                  <purchase-card
                    @purchaseUpdated="purchaseUpdated"
                    :purchase="purchase"
                    :owner="owner"
                  />
                </div>
              </draggable>
            </b-tab-item>

            <b-tab-item>
              <template slot="header">
                <b-icon icon="information-outline"></b-icon>
                <span class="has-text-weight-semibold">
                  Q&A
                  <!-- <b-tag rounded> {{ qaLength }} </b-tag>-->
                </span>
              </template>
              <questions-answers
                ref="questionsAnswers"
                :askableType="'project'"
                :askableId="$route.params.projectId"
                :owner="owner"
              />
            </b-tab-item>
          </b-tabs>

          <!-- Comments -->
          <comments
            :store-action-root="'projects/comments'"
            :action-parameter="$route.params.projectId"
            :commentable="'project'"
            :commentable-owner="owner"
            :user-id="user.id"
          />
        </div>
      </section>

      <!-- Modals -->
      <b-modal
        :active.sync="isNewCraftModalActive"
        :can-cancel="modalCancellable"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
        :width="1000"
      >
        <new-craft :project="project" />
      </b-modal>

      <b-modal
        :active.sync="isNewPurchaseModalActive"
        :can-cancel="modalCancellable"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <new-purchase :project="project" @purchaseCreated="updatePurchases" />
      </b-modal>

      <b-modal
        :active.sync="isUpdateProjectModalActive"
        :can-cancel="modalCancellable"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <update-project-modal @updated="setProject" :project="project" />
      </b-modal>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import PurchaseCard from '@/views/Project/Cards/PurchaseCard'
import NewCraft from './NewCraft.vue'
import NewPurchase from './NewPurchase.vue'
import Comments from '@/components/concerns/Comments'
import AddToCollection from '@/components/buttons/AddToCollection'
import CarouselHorizontal from '@/components/concerns/Images/CarouselHorizontal'
import UpdateProjectModal from '@/views/Project/UpdateProjectModal'
import QuestionsAnswers from '@/components/concerns/QuestionsAnswers'
import ThemeChanger from '@/components/buttons/ThemeChanger'
import ImageManagement from '@/components/concerns/Images/ImageManagement'
import AddTeacherButton from '@/components/buttons/AddTeacherButton'
import AttributeTitle from '@/components/Attributes/AttributeTitle'

import draggable from 'vuedraggable'
import * as moment from 'moment'
import { dateMixin } from '@/mixins/dateMixin.js'
import { backendMixin } from '@/mixins/backendMixin.js'
import { notificationMixin } from '@/mixins/notificationMixin.js'
import { themableMixin } from '@/mixins/themableMixin'
import TagList from '@/components/concerns/Tags/TagList'
import CraftCard from '@/views/Project/Cards/CraftCard'

export default {
  mixins: [dateMixin, notificationMixin, backendMixin, themableMixin],
  data () {
    return {
      project: null,
      items: null,
      user: null,
      crafts: [],
      purchases: [],
      images: [],
      selectedPurchase: null,
      purchasedItems: null,
      isNewCraftModalActive: false,
      isNewPurchaseModalActive: false,
      isAddQaModalActive: false,
      isEditQaModalActive: false,
      isPurchaseModalActive: false,
      isUpdateProjectModalActive: false,
      modalCancellable: [true, true, false],

      targetedQa: null
    }
  },
  components: {
    CraftCard,
    TagList,
    PurchaseCard,
    NewCraft,
    NewPurchase,
    Comments,
    AddToCollection,
    CarouselHorizontal,
    UpdateProjectModal,
    QuestionsAnswers,
    ThemeChanger,
    ImageManagement,
    draggable,
    AddTeacherButton,
    AttributeTitle
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'isLoggedIn',
      isOwner: 'isOwner',
      currentUser: 'currentUser'
    }),
    owner () {
      return this.isOwner(this.project.user_id)
    },
    themableTheme () {
      return this.project ? this.project.theme.name : null
    }
  },
  created () {
    this.fetchData()
  },
  watch: {
    $route: 'fetchData'
  },
  methods: {
    setProject (values) {
      this.project = values
    },
    fetchCrafts () {
      this.$store
        .dispatch('projects/crafts/index', this.$route.params.projectId)
        .then((resp) => {
          this.crafts = resp.data.data
        })
        .catch((response) => {
          // TODO: Handle error
          console.log(response.response)
          console.log('Error')
        })
    },
    fetchPurchases () {
      this.$store
        .dispatch('projects/purchases/index', this.$route.params.projectId)
        .then((resp) => {
          this.purchases = resp.data.data
        })
        .catch((_) => {
          // TODO: Handle error
          console.log('Error')
        })
    },
    fetchImages () {
      this.$store
        .dispatch('images/index', {
          imageableType: 'project',
          imageableId: this.$route.params.projectId
        })
        .then((resp) => {
          this.images = resp.data.data
        })
        .catch((error) => {
          console.log('error')
          console.log(error.response)
        })
    },
    fetchData () {
      this.$store
        .dispatch('projects/show', this.$route.params.projectId)
        .then((resp) => {
          this.setProject(resp.data.data.attributes)
          this.$store
            .dispatch('users/show', this.project.user_username)
            .then((resp) => {
              this.user = resp.data.data.attributes
            })
            .catch((_) => {
              // TODO: Handle error
              console.log('Error')
            })
        })
        .catch((error) => {
          this.handleError(error, this.$route)
        })
      this.fetchCrafts()
      this.fetchPurchases()
      this.fetchImages()
    },
    updatePurchases (values) {
      this.purchases = values
    },
    purchaseUpdated (values) {
      this.purchases = values
    },
    formatDate (date) {
      return moment(date).format('LL')
    },
    craftDragged (e) {
      this.$store
        .dispatch('crafts/update', {
          craft: e.moved.element.id,
          payload: { project_order_position: e.moved.newIndex }
        })
        .then((_) => {
          this.fetchCrafts()
        })
        .catch((error) => {
          // TODO: Handle error
          console.log(error.response.data)
        })
    },
    purchaseDragged (e) {
      this.$store
        .dispatch('purchases/update', {
          purchase: e.moved.element.id,
          payload: { project_order_position: e.moved.newIndex }
        })
        .then((_) => {
          this.fetchPurchases()
        })
        .catch((error) => {
          // TODO: Handle error
          console.log(error.response.data)
        })
    }
  }
}
</script>

<style lang="scss" scoped></style>
